import { useCallback, useEffect, useRef, useState } from "react";
import {
  createCourse,
  deleteCourse,
  downBottomOrderNumber,
  downOrderNumber,
  getListCourse,
  updateCourse,
  upOrderNumber,
  upTopOrderNumber,
} from "../../api/course";
import { useQuery } from "react-query";
import { isNumeric } from "../../utils";

const useCourseMaster = () => {
  const [arrCourses, setArrCourses] = useState([]);
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [newCourse, setNewCourse] = useState({
    name: "",
    cost: "",
    therapist_amount: "",
    duration: "",
    remarks: "",
    display: 1,
  });

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [editCourse, setEditCourse] = useState(null);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [isPopupOpenUpdate, setIsPopupOpenUpdate] = useState(false);
  const [isPopupOpenModalOption, setIsPopupOpenModalOption] = useState(false);
  const [isPopupOpenDelete, setIsPopupOpenDelete] = useState(false);
  const [isPopupOpenToggle, setIsPopupOpenToggle] = useState(false);
  const [courseToToggle, setCourseToToggle] = useState(null);
  const [nextDisplayValue, setNextDisplayValue] = useState(1);
  const [focusState, setFocusState] = useState({
    name: false,
    cost: false,
    therapist_amount: false,
    duration: false,
    remarks: false,
  });

  const nameInputRef = useRef(null);

  const [errors, setErrors] = useState({
    name: "",
    cost: "",
    therapist_amount: "",
    duration: "",
    remarks: "",
    display: "",
  });

  const [errorsUpdate, setErrorsUpdate] = useState({
    name: "",
    cost: "",
    therapist_amount: "",
    duration: "",
    remarks: "",
    display: "",
  });

  const { data, refetch } = useQuery(
    ["courses", page, rowsPerPage],
    () => {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      return getListCourse(params);
    },
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (data) {
      setArrCourses(transformCourseData(data.data.data));
      setTotal(data.data.total);
    }
  }, [data]);

  useEffect(() => {
    setSortedData(arrCourses.sort((a, b) => b.order_number - a.order_number));
  }, [arrCourses]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const transformCourseData = (data) => {
    return data.map((course) => ({
      order_number: course.order_number,
      id: course.id.toString(),
      name: course.name,
      cost: parseInt(course.cost),
      therapist_amount: parseInt(course.therapist_amount),
      duration: course.duration,
      remarks: course.remarks || "",
      display: Number(course.display ?? 1),
      options: course.options,
    }));
  };

  const handleSort = useCallback((columnIndex) => {
    const isSorted = activeSortColumn === columnIndex;
    const direction = isSorted
      ? sortColumnDirection === "asc"
        ? "desc"
        : "asc"
      : "desc";
    const sorted = [...sortedData].sort((a, b) => {
      let aValue = a[Object.keys(a)[columnIndex]];
      let bValue = b[Object.keys(b)[columnIndex]];

      // if aValue and bValue are numberic, sort parseInt
      if (isNumeric(aValue) && isNumeric(bValue)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData(sorted);
    setActiveSortColumn(columnIndex);
    setSortColumnDirection(direction);
  }, [activeSortColumn, sortColumnDirection, sortedData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCourse({ ...newCourse, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddCourse = async () => {
    try {
      const newCourseData = {
        name: newCourse.name,
        cost: newCourse.cost,
        therapist_amount: newCourse.therapist_amount,
        duration: newCourse.duration,
        remarks: newCourse.remarks,
        display: newCourse.display,
      };

      const response = await createCourse(newCourseData);

      if (response && response.data) {
        refetch();
        setNewCourse({
          name: "",
          cost: "",
          therapist_amount: "",
          duration: "",
          remarks: "",
          display: 1,
        });
        setErrors({
          name: "",
          cost: "",
          therapist_amount: "",
          duration: "",
          remarks: "",
          display: "",
        });
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrors((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
    }
  };

  const handleUpdateCourse = async (updatedCourse) => {
    try {
      const { id, name, cost, therapist_amount, duration, remarks, display } =
        updatedCourse;
      const updatedData = {
        name,
        cost: cost,
        therapist_amount: therapist_amount,
        duration: duration,
        remarks: remarks,
        display,
      };

      const response = await updateCourse(id, updatedData);

      if (response && response.data) {
        refetch();
        return true;
      } else {
        alert("エラーが発生しました。");
        return false;
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrorsUpdate((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
      return false;
    }
  };

  const handleDeleteCourse = async (id) => {
    try {
      const response = await deleteCourse(id);

      if (response.success) {
        refetch();
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      alert("エラーが発生しました。");
    }
  };

  const handleUpOrderNumber = useCallback(async (id) => {
    try {
      const response = await upOrderNumber(id);
      refetch();
      if (activeSortColumn !== 0) {
        handleSort(0); // sort by order number
      }
      alert(response?.message);
    } catch (error) {
      alert(error?.response?.data?.message || "エラーが発生しました。");
    }
  }, [activeSortColumn, handleSort, refetch]);

  const handleDownOrderNumber = useCallback(async (id) => {
    try {
      const response = await downOrderNumber(id);
      refetch();
      if (activeSortColumn !== 0) {
        handleSort(0); // sort by order number
      }
      alert(response?.message);
    } catch (error) {
      alert(error?.response?.data?.message || "エラーが発生しました。");
    }
  }, [activeSortColumn, handleSort, refetch]);

  const handleUpTopOrderNumber = useCallback(async (id) => {
    try {
      const response = await upTopOrderNumber(id);
      refetch();
      if (activeSortColumn !== 0) {
        handleSort(0); // sort by order number
      }
      alert(response?.message);
    } catch (error) {
      alert(error?.response?.data?.message || "エラーが発生しました。");
    }
  }, [activeSortColumn, handleSort, refetch]);

  const handleDownBottomOrderNumber = useCallback(async (id) => {
    try {
      const response = await downBottomOrderNumber(id);
      refetch();
      if (activeSortColumn !== 0) {
        handleSort(0); // sort by order number
      }
      alert(response?.message);
    } catch (error) {
      alert(error?.response?.data?.message || "エラーが発生しました。");
    }
  }, [activeSortColumn, handleSort, refetch]);

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    const maxPage = Math.floor(total / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(Math.min(page, maxPage));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFocus = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleBlur = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: false,
    }));
  };

  const handleEditClick = (course) => {
    setEditCourse(course);
    setIsPopupOpenUpdate(true);
  };

  const handleDeleteClick = (course) => {
    setCourseToDelete(course);
    setIsPopupOpenDelete(true);
  }

  const handleClosePopup = () => {
    setIsPopupOpenUpdate(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEditCourse((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorsUpdate((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleOpenTogglePopup = (course, nextValue) => {
    setCourseToToggle(course);
    setNextDisplayValue(nextValue);
    setIsPopupOpenToggle(true);
  };

  const handleConfirmToggleDisplay = async () => {
    if (!courseToToggle) return;
    try {
      const payload = {
        name: courseToToggle.name,
        cost: courseToToggle.cost,
        therapist_amount: courseToToggle.therapist_amount,
        duration: courseToToggle.duration,
        remarks: courseToToggle.remarks,
        display: nextDisplayValue,
      };
      const response = await updateCourse(courseToToggle.id, payload);
      if (response && response.data) {
        await refetch();
        setIsPopupOpenToggle(false);
        setCourseToToggle(null);
        return;
      }
      alert("エラーが発生しました。");
    } catch (error) {
      alert("エラーが発生しました。");
    } finally {
      setIsPopupOpenToggle(false);
    }
  };

  const handleToggleDisplay = (_, checked) => {
    setNewCourse((prev) => ({
      ...prev,
      display: checked ? 1 : 0,
    }));
    setErrors((prev) => ({ ...prev, display: "" }));
  };

  const handleToggleDisplayEdit = (_, checked) => {
    setEditCourse((prev) => ({
      ...prev,
      display: checked ? 1 : 0,
    }));
    setErrorsUpdate((prev) => ({ ...prev, display: "" }));
  };

  return {
    arrCourses,
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    newCourse,
    rowsPerPage,
    page,
    total,
    editCourse,
    courseToDelete,
    isPopupOpenUpdate,
    isPopupOpenModalOption,
    isPopupOpenDelete,
    isPopupOpenToggle,
    focusState,
    nameInputRef,
    errors,
    errorsUpdate,
    refetch,
    setIsPopupOpenUpdate,
    setIsPopupOpenModalOption,
    setIsPopupOpenDelete,
    setIsPopupOpenToggle,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handleInputChange,
    handleAddCourse,
    handleUpdateCourse,
    handleDeleteCourse,
    handleFocus,
    handleBlur,
    handleEditClick,
    handleDeleteClick,
    handleClosePopup,
    handleChange,
    handleToggleDisplay,
    handleToggleDisplayEdit,
    handleUpOrderNumber,
    handleDownOrderNumber,
    handleUpTopOrderNumber,
    handleDownBottomOrderNumber,
    handleOpenTogglePopup,
    handleConfirmToggleDisplay,
    courseToToggle,
    nextDisplayValue,
  };
};

export default useCourseMaster;
